import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ExpandedState } from "@tanstack/react-table";
import { AccountingReportCycleTotal } from "@common/constants";
import {
    SchedulesReportLayout,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import { useLedgers, useLedgersByEntityIds } from "@api/ledger.api";
import { Entity } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    ScheduleApplicationsDrawer,
    ScheduleApplicationsDrawerScope,
} from "@components/accounting/schedules/applications/ScheduleApplicationsDrawer";
import { AccountingReportFilters } from "../types";
import { mapSchedulesReportFilters } from "../accountingReportFilters.lib";
import {
    buildSchedulesReportDrillDownFilters,
    SchedulesReportDrillDownParams,
    SchedulesReportOpenScheduleParams,
    SchedulesReportTableCallbacks,
} from "./schedulesReportDrillDown.lib";
import { SchedulesReportRowsSelect } from "./SchedulesReportRowsSelect";
import {
    SchedulesReportView,
    SchedulesReportViewSchema,
} from "@common/types/views/schedulesReportView";
import {
    NamedView,
    ViewOptions,
    ViewOptionsSchema,
    ViewType,
} from "@common/types/views/views";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import {
    BUILT_IN_SCHEDULES_REPORT_VIEWS,
    SCHEDULES_REPORT_DEFAULT_COLUMNS,
    SCHEDULES_REPORT_DEFAULT_VIEW_FILTERS,
    SCHEDULES_REPORT_DEFAULT_VIEW_OPTIONS,
    normalizeSchedulesReportViewColumns,
} from "./BuiltInSchedulesReportViews";
import { buildSchedulesReportConfig } from "./schedulesReportViewConfig";
import {
    applySchedulesReportViewFilters,
    buildSchedulesReportViewFilters,
} from "./schedulesReportViewMapper";
import { pluralizeWithCount } from "@common/helpers/string";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { RecognitionMethodSelect } from "./RecognitionMethodSelect";
import { resolveSchedulesReportMethod } from "./schedulesReportMethodOptions";
import { RevenueSubModeSelect } from "./RevenueSubModeSelect";
import {
    buildSchedulesReportExtraPresets,
    DEFAULT_SCHEDULES_REPORT_DATE_PRESET,
    findMatchingSchedulesReportPreset,
    resolveSchedulesReportPresetRange,
    SchedulesReportDatePresetId,
} from "./schedulesReportDatePresets";
import { buildHoldingAccountOptions } from "./schedulesReportAddFilterOptions";
import { SchedulesReportParams } from "./schedulesReportTypes";
import {
    useSchedulesReportDateBoundsQuery,
    useSchedulesReportQuery,
} from "@api/schedulesReport.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { useConstant } from "@hooks/useConstant";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import {
    AccountingFiltersSetter,
    useAccountingTabContext,
} from "../useAccountingTab";
import {
    buildSchedulesReportLayoutParams,
    resolveCycleChange,
    resolveLayoutChange,
} from "./schedulesReportFilterRules";
import { SchedulesReportLayoutSelect } from "./SchedulesReportLayoutSelect";
import {
    resolveBuiltInSchedulesReportView,
    shouldPreserveIncomingDateRangeOnDeepLink,
} from "./schedulesReportDeepLink";
import {
    DEFAULT_SCHEDULES_REPORT_METHOD,
    DEFAULT_SCHEDULES_REPORT_LAYOUT,
    DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
    DEFAULT_SCHEDULES_REPORT_ROWS,
    useSchedulesReportParams,
} from "./useSchedulesReportParams";
import { SchedulesReportLedgerSelect } from "./SchedulesReportLedgerSelect";
import { SchedulesReportPageContent } from "./SchedulesReportPageContent";
import {
    buildSchedulesReportRows,
    getSchedulesReportDefaultExpandedState,
} from "./schedulesReportViewBuilder";
import { useSchedulesReportDownload } from "./useSchedulesReportDownload";

type SchedulesReportViewSelectDateBehavior =
    | "apply-built-in-preset"
    | "preserve-current-range";

export const SchedulesReportPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const {
        params: urlParams,
        datePreset,
        viewId,
        scheduleApplicationIds,
        clearScheduleApplicationIds,
        setParams,
        setDatePreset,
        clearViewId,
    } = useSchedulesReportParams();
    const { schedulesMilestoneBased: isMilestoneEnabled } = useTypedFlags();
    const layout = urlParams.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT;
    const rowsMode = urlParams.rows ?? DEFAULT_SCHEDULES_REPORT_ROWS;
    const revenueSubMode =
        urlParams.revenueSubMode ?? DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE;
    const method = resolveSchedulesReportMethod({
        method: urlParams.method,
        isMilestoneEnabled,
    });
    const [generationDate, setGenerationDate] = useState(() => new Date());
    const today = useConstant(() => new Date());

    // Everything except layout, rows, revenue sub-mode and method lives in the
    // shared accounting filters so the generic filter chips and saved views
    // own it.
    const params = useMemo(
        (): SchedulesReportParams => ({
            ...urlParams,
            scheduleApplicationIds: scheduleApplicationIds.length
                ? scheduleApplicationIds
                : undefined,
            ...mapSchedulesReportFilters(filters),
            holdingAccountIds: filters.holdingAccountIds,
        }),
        [urlParams, scheduleApplicationIds, filters],
    );

    // The report only accepts accrual ledgers, so the page resolves the
    // ledger itself (the shared hook would default to cash) and fires nothing
    // until the entity's ledgers are known.
    const { data: entityLedgers } = useLedgers(filters.entityId);
    const resolvedLedgerId = useMemo(() => {
        if (!entityLedgers) {
            return undefined;
        }
        const accrualLedgers = entityLedgers.filter(
            (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
        );
        return (
            accrualLedgers.find((ledger) => ledger.id === filters.ledgerId) ??
            accrualLedgers[0]
        )?.id;
    }, [entityLedgers, filters.ledgerId]);
    useEffect(() => {
        if (resolvedLedgerId && resolvedLedgerId !== filters.ledgerId) {
            setFilters((current) => ({
                ...current,
                ledgerId: resolvedLedgerId,
            }));
        }
    }, [resolvedLedgerId, filters.ledgerId, setFilters]);
    const reportFilters = useMemo(
        () => ({ ...filters, ledgerId: resolvedLedgerId }),
        [filters, resolvedLedgerId],
    );

    const report = useSchedulesReportQuery(reportFilters, params);
    // Same scope as the report: wait for it so one failure yields one toast.
    const dateBounds = useSchedulesReportDateBoundsQuery(
        reportFilters,
        params,
        { isEnabled: report.isSuccess },
    );

    const applyDatePreset = useCallback(
        (presetId: SchedulesReportDatePresetId) => {
            const range = resolveSchedulesReportPresetRange({
                presetId,
                today: new Date(),
                dateBounds: dateBounds.data,
            });
            setDatePreset(presetId);
            if (range) {
                setFilters((current) => ({
                    ...current,
                    startDate: range.start,
                    endDate: range.end,
                    isAllTime: undefined,
                }));
            }
        },
        [dateBounds.data, setDatePreset, setFilters],
    );

    const hasAppliedDefaultPresetRef = useRef(false);
    useEffect(() => {
        if (
            hasAppliedDefaultPresetRef.current ||
            datePreset !== null ||
            filters.startDate ||
            filters.endDate
        ) {
            return;
        }
        hasAppliedDefaultPresetRef.current = true;
        applyDatePreset(DEFAULT_SCHEDULES_REPORT_DATE_PRESET);
    }, [datePreset, filters.startDate, filters.endDate, applyDatePreset]);

    const handleLayoutChange = useCallback(
        (nextLayout: SchedulesReportLayout) => {
            const resolved = resolveLayoutChange({
                nextLayout,
                cycle: filters.cycle,
            });
            setParams(
                buildSchedulesReportLayoutParams({
                    layout: resolved.layout,
                    rowsMode,
                }),
            );
            if (resolved.cycle !== filters.cycle) {
                setFilters((current) => ({
                    ...current,
                    cycle: resolved.cycle,
                }));
            }
        },
        [filters.cycle, rowsMode, setFilters, setParams],
    );

    const handleSetFilters = useCallback<AccountingFiltersSetter>(
        (setter) => {
            const next = setter(filters);
            if (next.cycle !== filters.cycle) {
                const resolved = resolveCycleChange({
                    layout,
                    nextCycle: next.cycle,
                });
                if (resolved.layout !== layout) {
                    setParams(
                        buildSchedulesReportLayoutParams({
                            layout: resolved.layout,
                            rowsMode,
                        }),
                    );
                }
            }
            // One write: a separate setDatePreset would be overwritten by setFilters.
            const nextDatePreset =
                next.startDate !== filters.startDate ||
                next.endDate !== filters.endDate
                    ? (findMatchingSchedulesReportPreset({
                          start: next.startDate,
                          end: next.endDate,
                          today: new Date(),
                          dateBounds: dateBounds.data,
                      }) ?? undefined)
                    : (datePreset ?? undefined);
            setFilters(() => ({ ...next, datePreset: nextDatePreset }));
        },
        [
            filters,
            layout,
            rowsMode,
            datePreset,
            dateBounds.data,
            setFilters,
            setParams,
        ],
    );

    const [drillDownParams, setDrillDownParams] =
        useState<SchedulesReportDrillDownParams | null>(null);
    // The GL drawer seeds its filters on mount only, so remount per click.
    const [drillDownKey, setDrillDownKey] = useState(0);
    const drillDownFilters = useMemo<AccountingReportFilters | null>(
        () =>
            drillDownParams
                ? buildSchedulesReportDrillDownFilters({
                      ...drillDownParams,
                      baseFilters: {
                          entityId: filters.entityId,
                          ledgerId: filters.ledgerId,
                      },
                  })
                : null,
        [drillDownParams, filters.entityId, filters.ledgerId],
    );
    const lastDrillDownFiltersRef = useRef<AccountingReportFilters | null>(
        null,
    );
    if (drillDownFilters) {
        lastDrillDownFiltersRef.current = drillDownFilters;
    }
    const businessEntityIds = useBusinessNonMockEntityIds();
    const { data: ledgersByEntityId } =
        useLedgersByEntityIds(businessEntityIds);
    const selectedEntityIds = useMemo(() => {
        if (filters.entityIds && filters.entityIds.length > 0) {
            return filters.entityIds;
        }
        return selectedEntity ? [selectedEntity.id] : [];
    }, [filters.entityIds, selectedEntity]);
    const isEntityDisabled = useCallback(
        (entity: Entity) =>
            ledgersByEntityId !== undefined &&
            !(ledgersByEntityId[entity.id] ?? []).some(
                (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
            ),
        [ledgersByEntityId],
    );
    const handleEntityIdsChange = useCallback(
        (nextEntityIds: number[] | undefined) =>
            setFilters((current) => {
                const entityIds =
                    nextEntityIds && nextEntityIds.length > 0
                        ? nextEntityIds
                        : undefined;
                const anchorEntityId =
                    entityIds &&
                    current.entityId &&
                    entityIds.includes(current.entityId)
                        ? current.entityId
                        : (entityIds?.[0] ?? current.entityId);
                const anchorAccrualLedger = anchorEntityId
                    ? ledgersByEntityId?.[anchorEntityId]?.find(
                          (ledger) =>
                              ledger.basis === AccountingLedgerBasis.accruals,
                      )
                    : undefined;
                return {
                    ...current,
                    entityIds,
                    entityId: anchorEntityId,
                    ledgerId: anchorAccrualLedger?.id ?? current.ledgerId,
                };
            }),
        [setFilters, ledgersByEntityId],
    );
    const drillDownEntityIds = selectedEntityIds;
    const reportPeriodStart = report.data?.periods[0]?.startDate;
    const reportPeriodEnd = report.data?.periods.at(-1)?.endDate;
    const [scheduleScope, setScheduleScope] =
        useState<ScheduleApplicationsDrawerScope | null>(null);
    const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);

    const tableCallbacks = useMemo<SchedulesReportTableCallbacks>(
        () => ({
            onDrillDown: (drillParams) => {
                setDrillDownKey((key) => key + 1);
                setDrillDownParams(drillParams);
            },
            onOpenSchedule: (target: SchedulesReportOpenScheduleParams) => {
                setScheduleScope((current) => ({
                    key: (current?.key ?? 0) + 1,
                    params: {
                        ...target,
                        ...(!target.applicationIds && {
                            periodStart: reportPeriodStart,
                        }),
                        periodEnd: reportPeriodEnd,
                    },
                }));
                setIsScheduleDrawerOpen(true);
            },
        }),
        [reportPeriodEnd, reportPeriodStart],
    );

    const [cachedViewOptions, setCachedViewOptions] =
        useWorkspaceUserCache<ViewOptions>(
            WORKSPACE_USER_CACHE_KEYS.SCHEDULES_REPORT_PAGE_VIEW_OPTIONS_CACHE_KEY,
            ViewOptionsSchema,
        );
    const [viewOptions, setViewOptions] = useState<ViewOptions>(
        cachedViewOptions ?? SCHEDULES_REPORT_DEFAULT_VIEW_OPTIONS,
    );
    const applyViewOptions = useCallback(
        (options: ViewOptions | null | undefined) => {
            const next = options ?? SCHEDULES_REPORT_DEFAULT_VIEW_OPTIONS;
            setViewOptions(next);
            setCachedViewOptions(next);
        },
        [setCachedViewOptions],
    );

    const [cachedViewColumns, setCachedViewColumns] = useWorkspaceUserCache<
        SchedulesReportView["columns"]
    >(
        WORKSPACE_USER_CACHE_KEYS.SCHEDULES_REPORT_PAGE_VIEW_COLUMNS_CACHE_KEY,
        SchedulesReportViewSchema.shape.columns,
    );
    const [viewColumns, setViewColumns] = useState<
        SchedulesReportView["columns"]
    >(cachedViewColumns ?? SCHEDULES_REPORT_DEFAULT_COLUMNS);
    const applyViewColumns = useCallback(
        (columns: SchedulesReportView["columns"] | undefined) => {
            const next = normalizeSchedulesReportViewColumns(columns);
            setViewColumns(next);
            setCachedViewColumns(next);
        },
        [setCachedViewColumns],
    );

    const currentView = useMemo<SchedulesReportView>(
        () => ({
            type: ViewType.SCHEDULES_REPORT,
            columns: viewColumns,
            filters: buildSchedulesReportViewFilters({
                params,
                datePreset,
                filters,
            }),
            options: viewOptions,
        }),
        [params, datePreset, filters, viewOptions, viewColumns],
    );
    const viewConfig = useMemo(
        () =>
            buildSchedulesReportConfig({
                periods: report.data?.periods ?? [],
                layout,
            }),
        [report.data?.periods, layout],
    );
    // Column settings and density edits arrive here; they never touch filters.
    const handleViewChange = useCallback(
        (changed: SchedulesReportView) => {
            applyViewOptions(changed.options);
            applyViewColumns(changed.columns);
        },
        [applyViewOptions, applyViewColumns],
    );
    const deepLinkedBuiltInView = useMemo(
        () => resolveBuiltInSchedulesReportView(viewId),
        [viewId],
    );
    const [appliedDeepLinkViewId, setAppliedDeepLinkViewId] = useState<
        string | null
    >(null);

    const applySelectedSchedulesReportView = useCallback(
        (
            selected: SchedulesReportView | NamedView<SchedulesReportView>,
            dateBehavior: SchedulesReportViewSelectDateBehavior,
        ) => {
            applyViewOptions(selected.options);
            applyViewColumns(selected.columns);
            const state = applySchedulesReportViewFilters(selected.filters);
            setParams({
                layout: state.params.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT,
                rows: state.params.rows ?? DEFAULT_SCHEDULES_REPORT_ROWS,
                revenueSubMode:
                    state.params.revenueSubMode ??
                    DEFAULT_SCHEDULES_REPORT_REVENUE_SUB_MODE,
                method: state.params.method ?? DEFAULT_SCHEDULES_REPORT_METHOD,
            });
            setFilters((current) => ({
                ...current,
                scheduleTemplateIds: state.params.templateIds,
                holdingAccountIds: state.params.holdingAccountIds,
                scheduleGroups: state.params.groups,
                termStartFrom: state.params.termStartFrom,
                termStartTo: state.params.termStartTo,
                termEndFrom: state.params.termEndFrom,
                termEndTo: state.params.termEndTo,
                cycle: state.filters.cycle,
                classes: state.filters.classes,
                classFilterType: state.filters.classFilterType,
                counterpartiesIds: state.filters.counterpartiesIds,
                withEmptyOrUnverifiedCounterparty:
                    state.filters.withEmptyOrUnverifiedCounterparty,
                hasCounterparty: state.filters.hasCounterparty,
                minAmount: state.filters.minAmount,
                maxAmount: state.filters.maxAmount,
                ...(state.datePreset === null && {
                    startDate: state.filters.startDate,
                    endDate: state.filters.endDate,
                }),
            }));
            if (dateBehavior === "preserve-current-range") {
                return;
            }
            if (state.datePreset) {
                applyDatePreset(state.datePreset);
            } else {
                setDatePreset(null);
            }
        },
        [
            applyDatePreset,
            applyViewOptions,
            applyViewColumns,
            setDatePreset,
            setFilters,
            setParams,
        ],
    );

    const handleViewSelect = useCallback(
        (selected: SchedulesReportView | NamedView<SchedulesReportView>) => {
            applySelectedSchedulesReportView(selected, "apply-built-in-preset");
        },
        [applySelectedSchedulesReportView],
    );

    const deepLinkViewIdInFlightRef = useRef<string | null>(null);
    useEffect(() => {
        if (!viewId) {
            deepLinkViewIdInFlightRef.current = null;
            return;
        }
        if (
            !deepLinkedBuiltInView ||
            deepLinkViewIdInFlightRef.current === viewId
        ) {
            return;
        }
        deepLinkViewIdInFlightRef.current = viewId;
        setAppliedDeepLinkViewId(deepLinkedBuiltInView.id);
        const dateBehavior: SchedulesReportViewSelectDateBehavior =
            shouldPreserveIncomingDateRangeOnDeepLink({
                startDate: filters.startDate,
                endDate: filters.endDate,
            })
                ? "preserve-current-range"
                : "apply-built-in-preset";
        applySelectedSchedulesReportView(deepLinkedBuiltInView, dateBehavior);
        clearViewId();
    }, [
        applySelectedSchedulesReportView,
        clearViewId,
        deepLinkedBuiltInView,
        filters.endDate,
        filters.startDate,
        viewId,
    ]);

    const appliedCycle = report.data?.meta.appliedCycle;
    useEffect(() => {
        if (
            report.data &&
            appliedCycle &&
            filters.cycle !== appliedCycle &&
            (filters.cycle === undefined ||
                filters.cycle === AccountingReportCycleTotal.TOTAL)
        ) {
            setFilters((current) => ({ ...current, cycle: appliedCycle }));
        }
    }, [report.data, appliedCycle, filters.cycle, setFilters]);

    const rows = useMemo(
        () => (report.data ? buildSchedulesReportRows(report.data) : []),
        [report.data],
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const reportKey = useMemo(
        () => JSON.stringify({ filters, params }),
        [filters, params],
    );
    const expandedReportKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!report.data) {
            return;
        }
        setGenerationDate(new Date());
    }, [report.data]);

    // Reset expansion only when a report for a new query arrives, so background
    // refetches keep the user's manual expand/collapse choices.
    useEffect(() => {
        if (
            !report.data ||
            report.isPlaceholderData ||
            expandedReportKeyRef.current === reportKey
        ) {
            return;
        }
        expandedReportKeyRef.current = reportKey;
        setExpanded(getSchedulesReportDefaultExpandedState(rows));
    }, [report.data, report.isPlaceholderData, reportKey, rows]);

    const { isDownloading, download } = useSchedulesReportDownload({
        filters: reportFilters,
        params,
        rowVisibility: report.data?.presentation.rowVisibility,
    });

    const handleLedgerChange = useCallback(
        (ledgerId: string) =>
            setFilters((current) => ({ ...current, ledgerId })),
        [setFilters],
    );

    const toolbarActions = (
        <>
            <CollapseAllButton expanded={expanded} tableRef={tableRef} />
            <DownloadDropdown
                onDownload={(format) =>
                    selectedEntity &&
                    download({
                        entity: selectedEntity,
                        format,
                        date: filters.endDate,
                    })
                }
                loading={isDownloading}
                disabled={report.isLoading || !report.isSuccess}
            />
        </>
    );

    const holdingAccountOptions = useMemo(
        () =>
            buildHoldingAccountOptions(report.data?.meta.holdingAccounts ?? []),
        [report.data?.meta.holdingAccounts],
    );
    const extraDatePresets = useMemo(
        () =>
            buildSchedulesReportExtraPresets({
                today,
                dateBounds: dateBounds.data,
            }),
        [today, dateBounds.data],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_SCHEDULES_REPORT_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            <ViewToolbar<SchedulesReportView>
                view={currentView}
                onViewChange={handleViewChange}
                onViewSelect={handleViewSelect}
                viewConfig={viewConfig}
                builtInViews={BUILT_IN_SCHEDULES_REPORT_VIEWS}
                defaultFilters={SCHEDULES_REPORT_DEFAULT_VIEW_FILTERS}
                initialBaseViewId={
                    deepLinkedBuiltInView?.id ??
                    appliedDeepLinkViewId ??
                    undefined
                }
                baseViewCacheKey={
                    WORKSPACE_USER_CACHE_KEYS.SCHEDULES_REPORT_PAGE_BASE_VIEW_ID_CACHE_KEY
                }
            >
                {toolbarActions}
            </ViewToolbar>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={handleSetFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                hideSearch
                hideCreateJournalEntryButton
                showCyclesSelect
                hasEntityGrouping
                hasClassGrouping
                showCounterpartyFilter
                showAmountFilter
                showSchedulesReportFilters
                staticHoldingAccountOptions={holdingAccountOptions}
                filterButtonLabel="Add filter"
                extraDatePresets={extraDatePresets}
                cycleGroupAppend={({ className }) => (
                    <SchedulesReportRowsSelect
                        className={className}
                        value={rowsMode}
                        layout={layout}
                        onChange={(nextRows: SchedulesReportRows) =>
                            setParams({ rows: nextRows })
                        }
                    />
                )}
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        isEntityDisabled={isEntityDisabled}
                        disabledEntityTooltip="This entity has no accrual ledger."
                        onChange={(next) =>
                            handleEntityIdsChange(next.entityIds)
                        }
                    />
                }
                afterFiltersChildren={
                    scheduleApplicationIds.length > 0 && (
                        <ActiveFilterButton
                            label={
                                scheduleApplicationIds.length === 1
                                    ? "1 schedule"
                                    : pluralizeWithCount(
                                          "selected schedule",
                                          scheduleApplicationIds.length,
                                      )
                            }
                            resetFilter={clearScheduleApplicationIds}
                        />
                    )
                }
                beforeEntityChildren={
                    <>
                        <RevenueSubModeSelect
                            value={revenueSubMode}
                            onChange={(next) =>
                                setParams({ revenueSubMode: next })
                            }
                        />
                        <RecognitionMethodSelect
                            value={method}
                            isMilestoneEnabled={isMilestoneEnabled}
                            onChange={(next) => setParams({ method: next })}
                        />
                        <SchedulesReportLayoutSelect
                            value={layout}
                            onChange={handleLayoutChange}
                        />
                    </>
                }
                ledgerSelectOverride={
                    <SchedulesReportLedgerSelect
                        selectedEntity={selectedEntity}
                        selectedLedgerId={resolvedLedgerId}
                        onChange={handleLedgerChange}
                    />
                }
            />
            {selectedEntity && (
                <LoaderOnInitialLoading queryResult={report} dimWhileFetching>
                    {report.data && (
                        <SchedulesReportPageContent
                            data={report.data}
                            rows={rows}
                            filters={filters}
                            chrome={report.data.presentation.chrome}
                            numberFormat={report.data.presentation.numberFormat}
                            generationDate={generationDate}
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            tableRef={tableRef}
                            callbacks={tableCallbacks}
                            viewOptions={viewOptions}
                            viewColumns={viewColumns}
                        />
                    )}
                </LoaderOnInitialLoading>
            )}
            {lastDrillDownFiltersRef.current && (
                <GlDrilldownDrawer
                    key={drillDownKey}
                    open={drillDownParams !== null}
                    onClose={() => setDrillDownParams(null)}
                    filters={lastDrillDownFiltersRef.current}
                    entityIds={drillDownEntityIds}
                    ledgerBasis={ledgerBasis}
                />
            )}
            <ScheduleApplicationsDrawer
                open={isScheduleDrawerOpen}
                scope={scheduleScope}
                onClose={() => setIsScheduleDrawerOpen(false)}
            />
        </>
    );
};
